import * as THREE from 'three';

import { SkyState } from '../sky/Daylight';

/**
 * Dym: nad placem budowy i nad ogniskiem osady.
 *
 * **Mało, duże i wolno**, i to jest cała odpowiedź na pytanie „ile cząstek".
 * Gra renderuje w mały bufor skalowany całkowicie: przy `PIXELS_PER_UNIT = 24`
 * i przybliżeniu 2 heks ma na ekranie jakieś dwadzieścia pikseli bufora, więc
 * kłąb szeroki na 0,12 jednostki wypada na jakieś sześć. Trzysta drobin po pół
 * piksela nie byłoby dymem, tylko brudem na ekranie — to ta sama reguła, którą
 * zapisała trawa (`GrassDetail.ts`) i mgła (`ForestMist.ts`).
 *
 * **Cała animacja siedzi w vertex shaderze**, jak przy deszczu: klatka to jeden
 * uniform czasu. Geometria zmienia się wyłącznie wtedy, gdy zmienia się **zbiór
 * budów** — a to jest zdarzenie na minuty, nie na klatki.
 *
 * **Kłąb jest billboardem**, czyli quadem rozkładanym w shaderze wzdłuż osi
 * kamery. Rzut jest izometryczny i obraca się w poziomie, więc dym postawiony
 * na sztywno pokazałby przy obrocie swoją krawędź.
 *
 * Widać go tam, gdzie widać rusztowanie: warunkiem jest ta sama budowa
 * (`readyIn > 0`), więc jedno nie może zostać na polu bez drugiego.
 */

/** Ile kłębów na jedną budowę. */
const PUFFS = 14;

/**
 * Ile kłębów nad ogniskiem i jak wąska jest ta strużka wobec dymu budowy.
 *
 * **Ognisko to nie plac budowy i nie ma nim być.** Tam pracuje kilkunastu ludzi
 * i kurzy się od ziemi; tu pali się ogień, przy którym ktoś siedzi. Pięć kłębów
 * o połowie rozmiaru i dwóch trzecich krycia czyta się jak strużka znad dachu,
 * a nie jak pożar — przy czym nad osadą stoi ich tyle, ile jest budynków, więc
 * gęściej robi się samo tam, gdzie ludzie mieszkają ciasno.
 */
const HEARTH_PUFFS = 5;
const HEARTH_SCALE = 0.5;
const HEARTH_DIM = 0.62;

/**
 * Ile nad ziemią zaczyna się dym ogniska **modelu bez ustawionego komina**.
 *
 * Ta sama liczba, co wysokość płomienia w `Hearths` — ogień i jego dym wychodzą
 * z jednego miejsca, bo to jest jeden ogień — i w obu miejscach jest dziś tym
 * samym: odwrotem na bryłę, o której nikt jeszcze nie powiedział, gdzie ma
 * komin.
 */
const HEARTH_LIFT = 0.18;

/** Jak wysoko dochodzi kłąb, w jednostkach świata. */
const RISE = 0.75;

/** Promień kłębu u dołu i u szczytu — dym się rozchodzi, idąc w górę. */
const BORN_SIZE = 0.05;
const GROWN_SIZE = 0.23;

/**
 * Jak bardzo kłęby różnią się rozmiarem i tempem.
 *
 * **Bez tego dziewięć kłębów to dziewięć kopii jednego.** Ziarno jest już na
 * każdym z nich (służy do znoszenia), więc wariacja nic nie kosztuje — a dym,
 * w którym każdy kłąb rośnie inaczej i w innym tempie, przestaje wyglądać jak
 * animowana pętla.
 */
const SIZE_SPREAD = 0.45;
const SPEED_SPREAD = 0.35;

/**
 * Jak szybko kłąb obraca się wokół osi patrzenia.
 *
 * Obrót jest jedyną rzeczą, która odróżnia dwa kłęby o tym samym szumie stojące
 * obok siebie — bez niego widać, że to jedna tekstura powielona.
 */
const SPIN = 0.55;

/**
 * Ile ciemniejszy jest spód kłębu od jego szczytu.
 *
 * **Cieniowanie liczone w shaderze, a nie światłem sceny.** Kłąb jest
 * billboardem bez normalnych i nie ma czego oświetlać; wystarczy przyciemnić go
 * od dołu, bo światło w tej grze zawsze przychodzi z góry. Cień w drugą stronę
 * (od słońca w bok) wymagałby kierunku, który przy obracającym się rzucie
 * zmieniałby stronę kłębu w trakcie patrzenia.
 */
const UNDERSIDE = 0.42;

/** Ile razy na sekundę kłąb przechodzi całą drogę. */
const SPEED = 0.28;

/** Jak daleko od środka pola rodzą się kłęby. */
const SPREAD = 0.18;

/** Najgęstszy dym, jaki wolno narysować. */
const OPACITY = 0.34;

/** Ile dymu zostaje w nocy — jak przy deszczu, bo dym też nie jest cieniem. */
const NIGHT_FLOOR = 0.45;

const WHITE = new THREE.Color(0xffffff);

/** Jedna budowa: skąd dymi. */
export interface SmokeSite {
  id: string;
  base: THREE.Vector3;
}

/**
 * Jedno ognisko osady: skąd dymi i ile ma kominów.
 *
 * **Kominów bywa kilka na jednym dachu** — tyle, ile ustawiono modelowi
 * w panelu — i każdy dostaje własny słup. Kosztuje to pięć kłębów, czyli
 * dwadzieścia wierzchołków w tej samej geometrii i tym samym draw callu.
 *
 * `base` jest odwrotem na model, któremu nikt punktów nie ustawił: ziemia pod
 * budynkiem, z której dym idzie tak, jak szedł, zanim punkty istniały.
 */
export interface SmokeHearth {
  id: string;
  base: THREE.Vector3;
  vents: THREE.Vector3[];
}

/**
 * Ogniska rozwinięte na kominy.
 *
 * **Podniesienie o `HEARTH_LIFT` dotyczy wyłącznie tych bez punktów**: komin
 * ustawiony w panelu jest już na swojej wysokości, a dołożenie mu dwudziestu
 * centymetrów oznaczałoby dym idący spod dachu, na który ktoś przed chwilą
 * celował markerem.
 */
function vents(hearths: SmokeHearth[]): SmokeSite[] {
  const all: SmokeSite[] = [];
  for (const hearth of hearths) {
    if (hearth.vents.length === 0) {
      all.push({
        id: hearth.id,
        base: new THREE.Vector3(hearth.base.x, hearth.base.y + HEARTH_LIFT, hearth.base.z),
      });
      continue;
    }
    // Własny identyfikator na komin, bo z niego bierze się ziarno kłębów:
    // dwa kominy jednej chaty dymiłyby inaczej nierozróżnialnie zgodnie.
    hearth.vents.forEach((vent, at) => all.push({ id: `${hearth.id}#${at}`, base: vent }));
  }
  return all;
}

export class Smoke {
  readonly object: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private geometry: THREE.BufferGeometry;

  /**
   * Które budowy są w geometrii, po identyfikatorach i w kolejności.
   *
   * Po to, żeby nie przebudowywać buforów co klatkę: budowa dochodzi
   * i schodzi rzadko, a przepisywanie kilkuset wierzchołków sześćdziesiąt razy
   * na sekundę byłoby płaceniem za to, że nic się nie zmieniło.
   */
  private built = '';

  /**
   * Własny zegar, całkowany klatkami — nie czas świata.
   *
   * Ten sam powód, co przy deszczu: `fract(t * prędkość)` po godzinach gry
   * zaczyna gubić precyzję w `float`, czyli dym zaczyna skakać. Nie ma czego
   * tym synchronizować — dwaj gracze patrzący na jedną budowę mają zgadzać się
   * co do tego, **że** dymi, a nie co do tego, gdzie jest ten jeden kłąb.
   */
  private time = 0;

  constructor() {
    this.geometry = new THREE.BufferGeometry();

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      // Zasłaniany przez teren przed nim, ale sam nic nie zasłania: kłąb nie
      // może wyciąć dziury w tym, co narysuje się po nim.
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uOpacity: { value: OPACITY },
        uColor: { value: new THREE.Color(0xffffff) },
      },
      vertexShader: /* glsl */ `
        uniform float uTime;
        attribute vec2 aCorner;
        attribute float aSeed;
        /** Jak duży jest ten słup wobec dymu budowy, i jak gęsty. */
        attribute float aScale;
        attribute float aDim;
        varying vec2 vCorner;
        varying float vAlpha;
        varying float vSeed;
        varying float vLife;

        void main() {
          // Drugie ziarno z pierwszego: jedna liczba na kłąb wystarczy, a dwie
          // niezależne wartości z niej są tańsze niż drugi atrybut.
          float other = fract(aSeed * 7.31 + 0.137);

          // Ile ten kłąb ma już za sobą, 0 przy ziemi, 1 u szczytu. Tempo
          // różne dla każdego, więc słup nie pulsuje w jednym rytmie.
          float pace = 1.0 + (other - 0.5) * ${SPEED_SPREAD.toFixed(3)};
          float life = fract(uTime * ${SPEED.toFixed(3)} * pace + aSeed);

          // Rośnie i blednie idąc w górę, i jedno i drugie jest tym samym
          // zdaniem: dym się rozrzedza. Krótkie narastanie na starcie, żeby
          // kłąb nie pojawiał się przy ziemi z pełną siłą.
          float spread = 1.0 + (aSeed - 0.5) * ${SIZE_SPREAD.toFixed(3)};
          float size = mix(${BORN_SIZE.toFixed(3)}, ${GROWN_SIZE.toFixed(3)}, life) * spread * aScale;
          vAlpha = smoothstep(0.0, 0.18, life) * (1.0 - smoothstep(0.45, 1.0, life)) * aDim;

          // Znoszenie: każdy kłąb ma własny kierunek z ziarna, więc słup nie
          // jest słupem. Rośnie z wysokością — u ziemi dym jeszcze stoi.
          float angle = aSeed * 6.2831853;
          vec3 centre = vec3(
            position.x + cos(angle) * ${SPREAD.toFixed(3)} * life * aScale,
            position.y + life * ${RISE.toFixed(3)} * aScale,
            position.z + sin(angle) * ${SPREAD.toFixed(3)} * life * aScale
          );

          // Quad rozkładany w osiach kamery, nie świata — patrz nagłówek.
          vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
          vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);

          // Obrót wokół osi patrzenia: powolny, własny dla każdego kłębu i tym
          // szybszy, im młodszy — dym u wylotu kręci się, wyżej już tylko płynie.
          float spin = aSeed * 6.2831853 + uTime * ${SPIN.toFixed(3)} * (other - 0.5) * 2.0;
          vec2 turned = vec2(
            aCorner.x * cos(spin) - aCorner.y * sin(spin),
            aCorner.x * sin(spin) + aCorner.y * cos(spin)
          );
          vec3 world = centre + right * turned.x * size + up * turned.y * size;

          // Do fragmentu idzie **obrócony** róg: szum ma kręcić się razem
          // z kłębem, inaczej kłąb obraca się pod nieruchomym wzorem.
          vCorner = turned;
          vSeed = aSeed;
          vLife = life;
          gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform float uOpacity;
        uniform vec3 uColor;
        varying vec2 vCorner;
        varying float vAlpha;
        varying float vSeed;
        varying float vLife;

        float smokeHash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float smokeValue(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(smokeHash(i), smokeHash(i + vec2(1.0, 0.0)), u.x),
            mix(smokeHash(i + vec2(0.0, 1.0)), smokeHash(i + vec2(1.0, 1.0)), u.x),
            u.y
          );
        }

        void main() {
          // Miękka kula z kwadratu: brzeg gaśnie, róg znika całkiem.
          float soft = 1.0 - smoothstep(0.25, 0.5, length(vCorner));

          // Szum **gruby i dwuwarstwowy**, i grubość jest tu warunkiem, a nie
          // oszczędnością: kłąb ma na ekranie kilka pikseli bufora, więc drobna
          // oktawa nie jest detalem, tylko migotaniem. Dwie warstwy o różnych
          // skalach i przeciwnym dryfie dają wrażenie, że coś się w środku
          // przewala — jedna wygląda jak przesuwana tapeta.
          vec2 grain = vCorner * 4.5 + vec2(vSeed * 31.7, vSeed * 17.3);
          float puff =
            smokeValue(grain + vec2(0.0, -uTime * 0.35)) * 0.6 +
            smokeValue(grain * 2.1 + vec2(uTime * 0.21, 0.0)) * 0.4;

          // Szum zjada brzeg, a nie środek: kłąb ma zostać kłębem, tylko
          // z postrzępioną krawędzią. Robi to przez **próg wygaszania**, nie
          // przez mnożenie — mnożenie przyciemniłoby też środek, a tam dym jest
          // najgęstszy. Im starszy kłąb, tym głębiej próg wchodzi: tak rozpada
          // się dym, który się rozrzedził.
          float bite = mix(0.10, 0.50, vLife);
          float edge = bite * (1.0 - puff);
          float mask = smoothstep(edge, edge + 0.35, soft);

          // Spód ciemniejszy od szczytu. Światło w tej grze przychodzi z góry,
          // a billboard nie ma normalnej, którą można by o to zapytać.
          float lift = smoothstep(-0.5, 0.5, vCorner.y);
          vec3 colour = uColor * mix(${(1 - UNDERSIDE).toFixed(3)}, 1.0, lift);

          float alpha = vAlpha * mask * uOpacity;
          if (alpha < 0.01) discard;
          gl_FragColor = vec4(colour, alpha);
        }
      `,
    });

    this.object = new THREE.Mesh(this.geometry, this.material);
    this.object.frustumCulled = false;
    this.object.name = 'build-smoke';
    // Nad terenem i wodą, pod chmurami — jak deszcz, i z tego samego powodu.
    this.object.renderOrder = 4;
    this.object.visible = false;
  }

  /**
   * Jedna klatka dymu.
   *
   * `sites` przychodzi z pola budynków i jest tym samym zbiorem, z którego
   * biorą się paski postępu i rusztowania — jedna prawda o tym, co się buduje.
   * `hearths` przychodzi z tej samej listy, przefiltrowanej tą samą listą
   * rodzajów, co ogień (`Hearths.LIT`).
   *
   * **Dym nad ogniskiem widać zawsze**, w odróżnieniu od samego ognia, który
   * zapala się dopiero tam, gdzie gasną cienie. Dym jest tym, co robi ognisko,
   * a nie tym, co ono oświetla; zostaje mu tylko przyciemnienie w nocy, to samo,
   * które ma deszcz.
   */
  update(
    sites: SmokeSite[],
    hearths: SmokeHearth[],
    sky: SkyState,
    reveal: number,
    deltaTime: number
  ): void {
    // `reveal` to otwieranie się mgły po wejściu do świata: dopóki jest
    // zamknięta, nie ma nic do pokazania i nie ma czego liczyć.
    this.object.visible = (sites.length > 0 || hearths.length > 0) && reveal > 0;
    if (!this.object.visible) return;

    this.time += deltaTime;
    this.rebuild(sites, vents(hearths));

    this.material.uniforms.uTime.value = this.time;

    // Dym nie ma własnej barwy: odbija to, co świeci, jak smuga deszczu.
    // Rozjaśniony wobec mgły, bo ma być widoczny na tle terenu, nie nieba.
    this.material.uniforms.uColor.value
      .copy(sky.fogColor)
      .lerp(sky.night ? sky.skyColor : sky.sunColor, 0.35)
      .lerp(WHITE, 0.45);

    this.material.uniforms.uOpacity.value =
      OPACITY * reveal * (NIGHT_FLOOR + (1 - NIGHT_FLOOR) * sky.shadowStrength);
  }

  /**
   * Przebuduj bufory, gdy zbiór budów jest inny niż ten, który w nich siedzi.
   *
   * Porównanie po identyfikatorach, a nie po liczbie: budowa skończona w tej
   * samej klatce, w której ruszyła następna, dałaby tę samą długość listy przy
   * zupełnie innych miejscach.
   */
  private rebuild(sites: SmokeSite[], hearths: SmokeSite[]): void {
    // **Miejsce jedzie w stemplu razem z identyfikatorem.** Sam zbiór budów nie
    // wystarczy, odkąd komin należy do bryły: obóz podniesiony do wioski ma ten
    // sam identyfikator i inny dach, więc porównanie po samych nazwach
    // zostawiłoby dym nad kominem, którego już nie ma. Zaokrąglone, żeby
    // przesiadka budynku o ułamek milimetra nie przebudowywała buforów.
    const where = (site: SmokeSite) =>
      `${site.id}@${site.base.x.toFixed(2)},${site.base.y.toFixed(2)},${site.base.z.toFixed(2)}`;
    const stamp = `${sites.map(where).join('|')}#${hearths.map(where).join('|')}`;
    if (stamp === this.built) return;
    this.built = stamp;

    // Dwa rodzaje słupa, jedna geometria. Różnica jedzie **atrybutami**, a nie
    // gałęzią w shaderze: dym dalej nie wie, nad czym stoi, tak samo jak nie
    // wie, co się buduje.
    const columns = [
      { sites, puffs: PUFFS, scale: 1, dim: 1, lift: 0 },
      // Bez podniesienia: kolumna ognisk przychodzi tu już rozwinięta na kominy,
      // a te stoją na swoich wysokościach — patrz `vents`.
      { sites: hearths, puffs: HEARTH_PUFFS, scale: HEARTH_SCALE, dim: HEARTH_DIM, lift: 0 },
    ];

    const quads = sites.length * PUFFS + hearths.length * HEARTH_PUFFS;
    const positions = new Float32Array(quads * 4 * 3);
    const corners = new Float32Array(quads * 4 * 2);
    const seeds = new Float32Array(quads * 4);
    const scales = new Float32Array(quads * 4);
    const dims = new Float32Array(quads * 4);
    const indices = new Uint32Array(quads * 6);

    const CORNERS = [
      [-0.5, -0.5],
      [0.5, -0.5],
      [0.5, 0.5],
      [-0.5, 0.5],
    ];

    let quad = 0;
    for (const column of columns) {
      for (const site of column.sites) {
        for (let puff = 0; puff < column.puffs; puff += 1) {
          // Faza z miejsca w kolejce plus ziarno z identyfikatora: kłęby jednego
          // słupa idą po sobie, a dwa słupy obok siebie nie dymią zgodnie.
          const seed = (puff / column.puffs + hashOf(site.id)) % 1;

          for (let corner = 0; corner < 4; corner += 1) {
            const vertex = quad * 4 + corner;
            positions[vertex * 3] = site.base.x;
            positions[vertex * 3 + 1] = site.base.y + column.lift;
            positions[vertex * 3 + 2] = site.base.z;
            corners[vertex * 2] = CORNERS[corner][0];
            corners[vertex * 2 + 1] = CORNERS[corner][1];
            seeds[vertex] = seed;
            scales[vertex] = column.scale;
            dims[vertex] = column.dim;
          }

          const at = quad * 6;
          const base = quad * 4;
          indices[at] = base;
          indices[at + 1] = base + 1;
          indices[at + 2] = base + 2;
          indices[at + 3] = base;
          indices[at + 4] = base + 2;
          indices[at + 5] = base + 3;
          quad += 1;
        }
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aCorner', new THREE.BufferAttribute(corners, 2));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1));
    geometry.setAttribute('aDim', new THREE.BufferAttribute(dims, 1));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    this.geometry.dispose();
    this.geometry = geometry;
    this.object.geometry = geometry;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/** Ziarno z identyfikatora budynku, 0–1. Ta sama liczba przy każdym wejściu. */
function hashOf(id: string): number {
  let hash = 0;
  for (let at = 0; at < id.length; at += 1) {
    hash = (hash * 31 + id.charCodeAt(at)) % 100003;
  }
  return hash / 100003;
}
